/** The transport every seekr call goes through: credentialed fetch with
 * Django CSRF handling. Pure fetch — no DOM beyond document.cookie — so
 * feature modules (auth, consent, a future account form) stay thin wrappers
 * over one client.
 *
 * get/post also report unexpected failures (network errors, 403, 5xx) to
 * Sentry via ui/lib/report.ts — the single point every call passes through,
 * so auth.ts and consent.ts need no reporting code of their own. Expected
 * 4xx (wrong code, bad password, no marketing subscription) are not reported —
 * same threshold as the Worker's own classification in src/lib/report.ts.
 */

import { csrfToken } from "../lib/csrfToken";
import { reportError } from "../lib/report";

export interface Client {
  /** Base of the email-otp auth API. */
  authBase: string;
  /** Base of the marketing-consent API. */
  consentBase: string;
  /** "Who is signed in" — seekr's session read (v3, where seekr serves it). */
  currentUserUrl: string;
  /** Base of the social-auth login URLs (full-page navigations, not fetch). */
  socialAuthBase: string;
  /** Edge logout endpoint. */
  logoutUrl: string;
  /** Edge email+password sign-in endpoint (seekr's legacy login, proxied).
   * Its own field rather than a path off authBase: that base is the v4 OTP
   * API, and this is a v3 endpoint the edge republishes under a clean URL. */
  passwordLoginUrl: string;
  get(url: string): Promise<Response>;
  post(url: string, body?: Record<string, unknown>): Promise<Response>;
}

function stepFor(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/** A client whose URLs hang off `origin` — "" for same-origin pages served
 * by the edge itself; the widget passes the edge origin derived from its own
 * <script src>. */
export function createClient(origin = ""): Client {
  const csrfUrl = `${origin}/csrf/`;
  const currentUserUrl = `${origin}/api/v3/accounts/user/logged_in/`;
  const consentBase = `${origin}/api/v4/consents/marketing-notification-preferences`;
  const purposeUrl = `${consentBase}/category/`;

  // CSRF bootstrap: Django's POST endpoints need a csrftoken cookie, which
  // used to be guaranteed by the server-rendered page (@ensure_csrf_cookie).
  // On the edge nothing has set it yet, so before the first POST hit /csrf/
  // (which proxies an ensure_csrf_cookie view). Shared promise: concurrent
  // POSTs bootstrap once. Failures fall through — the POST then fails with
  // Django's CSRF error rather than a silent hang.
  let bootstrap: Promise<unknown> | null = null;

  function ensureCsrf(): Promise<unknown> {
    // Never conditional on a csrftoken already being visible: on a consumer page
    // that cookie may be seekr's own, from the apex it also serves, which the
    // edge is never sent — so trusting it sends a token that cannot match.
    bootstrap ??= fetch(csrfUrl, { credentials: "include" }).catch(
      () => undefined,
    );
    return bootstrap;
  }

  function reportIfUnexpected(url: string, res: Response): void {
    if (res.status === 403 || res.status >= 500) {
      reportError(
        origin,
        stepFor(url),
        new Error(`unexpected ${res.status}`),
        res.status,
      );
    }
  }

  return {
    authBase: `${origin}/api/v4/users/auth`,
    consentBase,
    currentUserUrl,
    socialAuthBase: `${origin}/social-auth/login`,
    logoutUrl: `${origin}/logout`,
    passwordLoginUrl: `${origin}/password-login`,

    async get(url) {
      let res: Response;
      try {
        res = await fetch(url, {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
      } catch (err) {
        // A transport failure on a read the flow makes for itself is
        // overwhelmingly a content blocker, an offline tab or a navigation
        // abort — none of which this code can act on or even tell apart. The
        // session read (fired on construction, so on every consumer page load)
        // and the consent purpose lookup (whose null keeps the checkbox's
        // static fallback label) are both already absorbed where they're
        // called, so neither reports. Nothing else is exempt: a transport
        // failure on anything a visitor actually asked for still reports, and
        // so does any HTTP response here, which reached the edge.
        if (url !== currentUserUrl && url !== purposeUrl) {
          reportError(origin, stepFor(url), err);
        }
        throw err;
      }
      reportIfUnexpected(url, res);
      return res;
    },

    async post(url, body) {
      await ensureCsrf();
      let res: Response;
      try {
        res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken(),
          },
          body: JSON.stringify(body ?? {}),
        });
      } catch (err) {
        reportError(origin, stepFor(url), err);
        throw err;
      }
      reportIfUnexpected(url, res);
      return res;
    },
  };
}
